import { Box, AppBar, Drawer, useMediaQuery, useTheme } from "@mui/material";
import { Routes, Route, useLocation } from "react-router-dom";
import { Header } from "../components/navigation/Header";
import { SideMenu } from "../components/navigation/SideMenu";
import zIndex from "../designTokens/zIndex";
import Login from "../components/Auth/Login";
import Callback from "../components/Auth/Callback";
import Dashboard from "./Dashboard";
import LifetimeAnalytic from "./LifetimeAnalytic";
import SearchThreads from "./SearchThreads";
import FriendsInteraction from "./FriendsInteraction";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";
import MyAccount from "./MyAccount";
import Admin from "./Admin";
import AdminAvatarUpload from "./AdminAvatarUpload";
import { AdminRoute } from "../components/Auth/AdminRoute";
// import { useDisclosure } from "@mantine/hooks"; // MUI로 교체
import { useIsMobile } from "../hooks/useIsMobile";
import { useState, useEffect, useMemo } from "react";
import MyPage from "./MyPage";
import UserPage from "./UserPage";
import ThreaderList from "./ThreaderList";
import CollectionPage from "./CollectionPage";
import AllCollection from "./AllCollection";
import PostThreads from "./PostThreads";
import { useThreadsState } from "../contexts/ThreadsContext";
import { useUser } from "../contexts/UserContext";
import { useWritingMode } from "../contexts/WritingModeContext";
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";

export const AppPresenter = () => {
  const { t } = useTranslation();

  // 사이드바 레이아웃 상수
  const SIDEBAR_WIDTH = 256;
  const SIDEBAR_LEFT_OFFSET = 16;
  const SIDEBAR_PADDING = 16;
  const MAIN_MARGIN_LEFT =
    SIDEBAR_WIDTH + SIDEBAR_LEFT_OFFSET + SIDEBAR_PADDING; // 256 + 16 + 16 = 288

  // UserContext에서 상태 가져오기
  const { profile, isLogin, userLoading, error } = useUser();

  // WritingMode Context에서 상태 가져오기
  const { isFocusMode, isTransitioning } = useWritingMode();

  // Navigation and UI states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggle = () => setDrawerOpen(!drawerOpen);
  const close = () => setDrawerOpen(false);
  const theme = useTheme();
  const isXL = useMediaQuery(theme.breakpoints.down("xl")); // xl 이하 (1440px 이하)
  const location = useLocation();
  const isPublicRoute =
    location.pathname.startsWith("/user/") ||
    location.pathname.startsWith("/collection/") ||
    location.pathname === "/all-collection";

  // PostThreads 페이지인지 확인
  const isPostThreadsPage = location.pathname === "/post-thread";

  // 네비게이션 상태 통합 계산
  const navigationState = useMemo(() => {
    const isMobile = isXL; // XL 이하
    const isDesktop = !isXL; // XL 초과

    // 사이드메뉴 표시 조건
    const shouldShowSidebar = isLogin && !isPublicRoute;

    // Focus 모드에서 사이드메뉴 숨김 조건 (모든 브레이크포인트에서)
    const shouldHideSidebarInFocus = isPostThreadsPage && isFocusMode;

    // Drawer 설정
    const drawerVariant = isMobile ? "temporary" : "persistent";
    const shouldDrawerOpen = isMobile ? drawerOpen : true;
    const shouldRenderSidebar = shouldShowSidebar && !shouldHideSidebarInFocus;

    return {
      isMobile,
      isDesktop,
      shouldShowSidebar,
      shouldHideSidebarInFocus,
      shouldRenderSidebar,
      drawerVariant,
      shouldDrawerOpen,
    };
  }, [
    isXL,
    isLogin,
    isPublicRoute,
    isPostThreadsPage,
    isFocusMode,
    drawerOpen,
  ]);

  // ThreadsContext에서 모든 lifetime 데이터 가져오기
  const { lifetimeThreadsList, isLifetimeLoading, loadingProgress } =
    useThreadsState();

  // breakpoint 변경 시 drawer 상태 관리
  useEffect(() => {
    // 모바일에서 데스크톱으로 변경되거나,
    // 페이지 라우트가 변경될 때 drawer를 닫음
    setDrawerOpen(false);
  }, [isXL, location.pathname]);

  // 현재 로딩 단계에 따른 메시지 결정
  const getCurrentLoadingMessage = () => {
    if (userLoading) {
      return t("loading.user");
    }

    if (isLogin && isLifetimeLoading) {
      // ThreadsContext의 loadingProgress 상태 활용
      switch (loadingProgress.stage) {
        case "cache-check":
        case "cached":
        case "updating":
          return t("loading.threads_data");
        case "insights":
          return t("loading.insights_data");
        case "interactions":
          return t("loading.interaction_data");
        case "analyzing":
          return t("loading.analyzing_data");
        case "complete":
          return t("loading.complete");
        default:
          return t("loading.threads_data");
      }
    }

    return t("loading.essential_data");
  };

  // 서브 메시지 결정 (진행 상황 표시)
  const getCurrentSubMessage = () => {
    if (isLogin && isLifetimeLoading && loadingProgress.subMessage) {
      return loadingProgress.subMessage;
    }
    return null;
  };

  // 글로벌 로딩 상태 계산
  const globalLoading = userLoading || (isLogin && isLifetimeLoading);

  // firstThreadDate 메모이제이션으로 불필요한 정렬 방지
  const firstThreadDate = useMemo(() => {
    if (!lifetimeThreadsList || lifetimeThreadsList.length === 0) {
      return null;
    }
    // 가장 빠른 날짜 찾기 (전체 정렬 대신 reduce 사용)
    return lifetimeThreadsList.reduce((earliest, thread) => {
      const threadDate = new Date(thread.timestamp);
      const earliestDate = new Date(earliest);
      return threadDate < earliestDate ? thread.timestamp : earliest;
    }, lifetimeThreadsList[0].timestamp);
  }, [lifetimeThreadsList]);

  return (
    <>
      {/* 글로벌 데이터 로딩 표시 */}
      <DataLoader
        msg={getCurrentLoadingMessage()}
        subMessage={getCurrentSubMessage()}
        isLoad={globalLoading}
        showElapsedTime={isLogin && isLifetimeLoading}
        isLogin={isLogin}
        isXL={isXL}
        isPublicRoute={isPublicRoute}
      />

      <Box sx={{ display: "flex" }}>
        <AppBar
          position="fixed"
          sx={{
            height: "64px",
            backgroundColor: "transparent",
            boxShadow: "none",
            zIndex: zIndex.header,
          }}
        >
          {/* position for GNB */}
          <Header
            userData={profile}
            isLogin={isLogin}
            opened={drawerOpen}
            onToggleNav={toggle}
            isPostThreadsPage={isPostThreadsPage}
          />
        </AppBar>
        {navigationState.shouldRenderSidebar && (
          <Drawer
            variant={navigationState.drawerVariant}
            anchor="left"
            open={navigationState.shouldDrawerOpen}
            onClose={
              navigationState.isMobile ? () => setDrawerOpen(false) : undefined
            }
            sx={{
              flexShrink: 0,
              display: "block",
              "& .MuiDrawer-paper": {
                width: navigationState.isMobile ? "100vw" : SIDEBAR_WIDTH,
                boxSizing: "border-box",
                backgroundColor: navigationState.isMobile
                  ? "rgba(255, 255, 255, 0.95)"
                  : "transparent",
                backdropFilter: navigationState.isMobile
                  ? "blur(10px)"
                  : "none",
                border: "none",
                top: navigationState.isMobile ? 0 : 80,
                left: navigationState.isMobile ? 0 : SIDEBAR_LEFT_OFFSET,
                height: navigationState.isMobile
                  ? "100vh"
                  : "calc(100% - 80px)",
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: zIndex.sideMenu,
              },
            }}
          >
            {profile && <SideMenu userData={profile} toggle={toggle} />}
          </Drawer>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            paddingTop: "64px",
            marginLeft:
              navigationState.shouldShowSidebar && navigationState.isDesktop
                ? `${MAIN_MARGIN_LEFT}px`
                : 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          className="app-main"
        >
          <Routes>
            <Route index path={"/"} element={<Login />} />
            <Route path="/callback" element={<Callback />} />
            <Route
              index
              path={"/dashboard"}
              element={
                <Dashboard
                  profile={profile}
                  userLoading={userLoading}
                  error={error}
                />
              }
            />
            <Route
              path="/analytic"
              element={<LifetimeAnalytic firstThreadDate={firstThreadDate} />}
            />
            <Route path="/search" element={<SearchThreads />} />
            <Route
              path="/friends-interaction"
              element={<FriendsInteraction />}
            />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route
              path="/account"
              element={<MyAccount firstThreadDate={firstThreadDate} />}
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/avatar-upload"
              element={
                <AdminRoute>
                  <AdminAvatarUpload />
                </AdminRoute>
              }
            />
            <Route
              path="/my-page"
              element={<MyPage firstThreadDate={firstThreadDate} />}
            />
            <Route
              path="/my-collection"
              element={<MyPage firstThreadDate={firstThreadDate} />}
            />
            <Route
              path="/my-collection/:collectionId"
              element={<CollectionPage isMe={true} />}
            />
            <Route path="/user/:userID" element={<UserPage />} />
            <Route path="/threaders" element={<ThreaderList />} />
            <Route
              path="/collection/:collectionId"
              element={<CollectionPage isMe={false} />}
            />
            <Route path="/all-collection" element={<AllCollection />} />
            <Route path="/post-thread" element={<PostThreads />} />
          </Routes>
        </Box>
      </Box>
    </>
  );
};
